import './companyField.scoped.scss'
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import * as yup from "yup"
import { toast } from "react-hot-toast"
import { AxiosError } from "axios"
import { useSicalcApiService } from "services/sicalcApi/sicalcApiService"
import { getServerErrorMessage } from "services/sicalcApi/errors/getServerErrorMessage"
import { getServerFieldErrors } from "services/sicalcApi/errors/getServerFieldErrors"
import { CompanyResponse } from "services/sicalcApi/responses/companyResponse"
import { CreateCompanyRequest } from "services/sicalcApi/requests/createCompanyRequest"
import { ServerFieldError } from "utils/formFieldErrors"
import { TextField } from "components/common/formFields/textField/textField"
import { TextAreaField } from "components/common/formFields/textAreaField/textAreaField"
import { SearchableSelectField } from "components/common/formFields/SearchableSelectField/SearchableSelectField"
import {
  CountrySelectField,
  defaultCountryName,
} from "components/common/formFields/countrySelectField/countrySelectField"
import { ClearButton } from "components/common/buttons/clearButton/clearButton"

/**
 * A company picked from a list, or a new one filled in on the spot. Forms using
 * this include these values in their own, and the validation below in their schema.
 */
export interface CompanyFieldValues {
  companyId: string
  /** True while the user fills in a new company instead of picking one from the list */
  isNewCompany: boolean
  companyName: string
  organizationNo: string
  invoiceAddress: string
  invoicePostCode: string
  invoiceCity: string
  invoiceCountry: string
}

export const initialCompanyFieldValues: CompanyFieldValues = {
  companyId: '',
  isNewCompany: false,
  companyName: '',
  organizationNo: '',
  invoiceAddress: '',
  invoicePostCode: '',
  invoiceCity: '',
  invoiceCountry: defaultCountryName,
}

const newCompanyFields: (keyof CompanyFieldValues)[] = [
  "companyName",
  "organizationNo",
  "invoiceAddress",
  "invoicePostCode",
  "invoiceCity",
]

// Matches the limits on the server's CreateCompanyRequest
const maxLength = 250
const tooLongMessage = `For langt (maks ${maxLength} tegn)`
const requiredText = yup.string().trim().required("Påkrevd").max(maxLength, tooLongMessage)
const optionalText = yup.string().trim().max(maxLength, tooLongMessage)

/** Spaces are allowed while typing, e.g. "923 609 016", and removed before sending */
const digitsAndSpaces = /^[\d\s]*$/
const digitsOnlyMessage = "Kan bare inneholde siffer"
const removeWhitespace = (value: string) => value.replace(/\s/g, "")

/** Spread into the form's yup schema */
export const companyFieldValidation = {
  companyId: yup.string().when("isNewCompany", {
    is: false,
    then: yup.string().required("Velg et firma, eller legg til et nytt"),
  }),
  companyName: yup.string().when("isNewCompany", {
    is: true,
    then: requiredText,
  }),
  organizationNo: yup.string().when("isNewCompany", {
    is: true,
    then: requiredText.matches(digitsAndSpaces, digitsOnlyMessage),
  }),
  invoiceAddress: optionalText,
  invoicePostCode: optionalText.matches(digitsAndSpaces, digitsOnlyMessage),
  invoiceCity: optionalText,
}

const toCreateCompanyRequest = (values: CompanyFieldValues): CreateCompanyRequest => ({
  companyName: values.companyName.trim(),
  organizationNo: removeWhitespace(values.organizationNo),
  // The database does not accept nulls here, so send empty strings
  invoiceAddress: values.invoiceAddress.trim(),
  invoicePostCode: removeWhitespace(values.invoicePostCode),
  invoiceCity: values.invoiceCity.trim(),
  invoiceCountry: values.invoiceCountry,
})

/** Shown when the server says the new company already exists */
const conflictMessages: Partial<Record<keyof CompanyFieldValues, string>> = {
  organizationNo: "Et firma med dette organisasjonsnummeret finnes allerede. Velg det i listen over firmaer.",
  companyName: "Et firma med dette navnet finnes allerede. Velg det i listen over firmaer.",
}

/** Match the server's duplicate errors to the company fields, in Norwegian */
const toConflicts = (error: unknown, values: CompanyFieldValues): ServerFieldError<CompanyFieldValues>[] =>
  getServerFieldErrors(error).flatMap(serverError => {
    const field = newCompanyFields.find(f => f.toLowerCase() === serverError.field.toLowerCase())
    return field !== undefined
      ? [ {
        field,
        value: values[field],
        message: conflictMessages[field] ?? serverError.message,
      } ]
      : []
  })

export type CreateCompanyResult =
  | { companyId: string }
  | { conflicts: ServerFieldError<CompanyFieldValues>[] }
  | { errorMessage: string }

/** The companies to pick from, and a way to create a new one that adds it to the list */
export const useCompanies = () => {
  const { companyApi } = useSicalcApiService()
  const [ companies, setCompanies ] = useState<CompanyResponse[]>()

  const getCompanies = useCallback(async () => {
    try {
      const response = await companyApi.getAllWithoutUsers()
      return response.companies
    } catch {
      toast.error("Kunne ikke hente firmaene")
      return []
    }
  }, [companyApi])

  useEffect(() => {
    // Ignore a response that arrives after the form is gone or a newer request started
    let ignore = false
    getCompanies().then(companies => {
      if (!ignore) {
        setCompanies(companies)
      }
    })
    return () => {
      ignore = true
    }
  }, [getCompanies])

  const createCompany = useCallback(async (values: CompanyFieldValues): Promise<CreateCompanyResult> => {
    try {
      const company = await companyApi.create(toCreateCompanyRequest(values))
      setCompanies(previous => [ ...(previous ?? []), company ])
      return { companyId: company.id }
    } catch (error) {
      const conflicts = error instanceof AxiosError && error.response?.status === 409
        ? toConflicts(error, values)
        : []

      return conflicts.length > 0
        ? { conflicts }
        : { errorMessage: getServerErrorMessage(error) ?? "Kunne ikke opprette firmaet" }
    }
  }, [companyApi])

  return {
    companies,
    createCompany,
  }
}

/** The parts of a formik form this field uses */
interface CompanyFieldForm {
  values: CompanyFieldValues
  handleChange: (event: ChangeEvent<any>) => void
  handleBlur: (event: FocusEvent<any>) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void
}

export interface CompanyFieldProps {
  /** E.g. "Firma*" or "Fakturamottaker*" */
  label: string
  /** Heading over the new company fields, "Nytt firma" by default */
  newCompanyHeading?: string
  form: CompanyFieldForm
  companies: CompanyResponse[] | undefined
  isInvalid: (field: keyof CompanyFieldValues) => boolean
  errorsFor: (field: keyof CompanyFieldValues) => string | undefined
}

export const CompanyField = (props: CompanyFieldProps) => {
  const { form, isInvalid, errorsFor } = props

  const companyOptions = useMemo(() => props.companies
    ?.map(company => ({
      label: company.companyName,
      value: company.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'nb')),
  [props.companies])

  const showNewCompanyFields = () => {
    // A previous submit marks every field as touched. Don't greet the user with errors
    newCompanyFields.forEach(field => form.setFieldTouched(field, false, false))
    form.setFieldValue("isNewCompany", true)
  }

  const showCompanyList = () => {
    form.setFieldValue("isNewCompany", false)
  }

  if (!form.values.isNewCompany) {
    return (
      <div className="column gap1">
        <label className="column">
          <span className="form-label">{props.label}</span>
          <SearchableSelectField
            options={companyOptions}
            isLoading={props.companies === undefined}
            value={companyOptions?.find(option => option.value === form.values.companyId)}
            isInvalid={isInvalid("companyId")}
            onChange={value => form.setFieldValue("companyId", value ?? "")}
            onBlur={() => form.setFieldTouched("companyId", true)}
          />
        </label>
        {isInvalid("companyId") && (
          <div className="validation-error">{errorsFor("companyId")}</div>
        )}
        <div className="row">
          <ClearButton iconName="plus" onClick={showNewCompanyFields}>Nytt firma</ClearButton>
        </div>
      </div>
    )
  }

  return (
    <fieldset className="new-company column gap3">
      <legend className="form-label">{props.newCompanyHeading ?? "Nytt firma"}</legend>
      <TextField
        label="Firmanavn*"
        name="companyName"
        autoComplete="organization"
        autoFocus
        value={form.values.companyName}
        isInvalid={isInvalid("companyName")}
        errors={errorsFor("companyName")}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
      />
      <TextField
        label="Organisasjonsnummer*"
        name="organizationNo"
        inputMode="numeric"
        autoComplete="off"
        value={form.values.organizationNo}
        isInvalid={isInvalid("organizationNo")}
        errors={errorsFor("organizationNo")}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
      />

      <span className="sub-heading">Fakturaadresse</span>
      <TextAreaField
        label="Adresse"
        name="invoiceAddress"
        rows={2}
        autoComplete="billing street-address"
        value={form.values.invoiceAddress}
        isInvalid={isInvalid("invoiceAddress")}
        errors={errorsFor("invoiceAddress")}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
      />
      <div className="row gap3">
        <div className="post-code">
          <TextField
            label="Postnummer"
            name="invoicePostCode"
            inputMode="numeric"
            autoComplete="billing postal-code"
            value={form.values.invoicePostCode}
            isInvalid={isInvalid("invoicePostCode")}
            errors={errorsFor("invoicePostCode")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </div>
        <div className="grow">
          <TextField
            label="Poststed"
            name="invoiceCity"
            autoComplete="billing address-level2"
            value={form.values.invoiceCity}
            isInvalid={isInvalid("invoiceCity")}
            errors={errorsFor("invoiceCity")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </div>
      </div>
      <label className="column">
        <span className="form-label">Land</span>
        <CountrySelectField
          value={form.values.invoiceCountry}
          onChange={countryName => form.setFieldValue("invoiceCountry", countryName)}
          onBlur={() => form.setFieldTouched("invoiceCountry", true)}
        />
      </label>

      <div className="row">
        <ClearButton iconName="format-list-bulleted" onClick={showCompanyList}>
          Velg eksisterende firma
        </ClearButton>
      </div>
    </fieldset>
  )
}
